<template>
  <div class="landing-root">

    <!-- HERO -->
    <section class="hero-section">
      <!-- decor blobs -->
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="grid-bg"></div>

      <v-container class="fill-height">
        <v-row align="center" justify="space-between">
          <!-- LEFT: copy -->
          <v-col cols="12" md="6" lg="6" class="text-md-left hero-left">
            <div class="badge">
              <v-icon left size="18" style="color: #ff9800;">mdi-lightning-bolt</v-icon>
              IA aplicada ao seu negócio
            </div>

            <h1 class="hero-title">
              Acelere suas ideias com
              <span class="gradient-text">Inteligência Artificial</span>
            </h1>

            <p class="hero-subtitle">
              A <strong>DIRECIONA TECH</strong> transforma processos em experiências inteligentes — de chatbots avançados a geração de imagens e automações.
            </p>

            <div class="hero-cta">
              <v-btn x-large color="primary" class="text-none font-weight-bold" depressed @click="$router.push('/chat')">
                Começar agora
              </v-btn>
              <v-btn x-large outlined class="text-none font-weight-bold ml-2" @click="scrollTo('#features')" style="color: #e5e7eb;">
                Ver recursos
              </v-btn>
            </div>

            <!-- mini stats -->
            <div class="stats" v-intersect="onStatsIntersect">
              <div class="stat">
                <span class="n">{{ usersDisplay }}</span>
                <span>usuários</span>
              </div>
              <div class="dot"></div>
              <div class="stat">
                <span class="n">{{ uptimeDisplay }}</span>
                <span>uptime</span>
              </div>
              <div class="dot"></div>
              <div class="stat">
                <span class="n">{{ latencyDisplay }}</span>
                <span>latência média</span>
              </div>
            </div>
          </v-col>

          <!-- RIGHT: product preview -->
          <v-col cols="12" md="6" lg="5" class="hero-right">
            <div class="preview-stack">
              <div class="preview-card glass" aria-hidden="true">
                <div class="preview-header">
                  <div class="dot r"></div>
                  <div class="dot y"></div>
                  <div class="dot g"></div>
                  <span class="preview-title">Assistente de Chat</span>
                </div>
                <div class="preview-body">
                  <div class="msg me">{{ assistenteChat[0] }}</div>
                  <div class="msg bot">
                    <v-icon left small color="primary">mdi-robot-outline</v-icon>
                    {{ assistenteChat[1] }} <br />Hoje é {{ new Date().toLocaleDateString("pt-BR") }}. Como posso ajudar você?
                  </div>
                  <div class="msg me">{{ assistenteChat[2] }}</div>
                  <div class="msg bot">
                    <v-icon left small color="primary">mdi-palette</v-icon>
                    {{ assistenteChat[3] }}
                  </div>
                </div>
                <div class="preview-footer">
                  <div class="input-pill">
                    <v-icon left small>mdi-message-outline</v-icon>
                    Escreva um prompt...
                  </div>
                  <v-btn icon color="primary"><v-icon>mdi-send</v-icon></v-btn>
                </div>
              </div>

              <div class="preview-float">
                <div class="float-card">
                  <v-icon color="primary" size="18">mdi-shield-check</v-icon>
                  <span>LGPD Ready</span>
                </div>
                <div class="float-card">
                  <v-icon color="primary" size="18">mdi-rocket-launch</v-icon>
                  <span>Deploy contínuo</span>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- TRUST / MARQUEE -->
    <section class="marquee">
      <div class="marquee-track">
        <span v-for="(logo, i) in marquee" :key="i" class="marquee-item">
          <v-icon left size="20" class="gradient-text">{{ logo.icon }}</v-icon>{{ logo.label }}
        </span>
        <!-- duplicate for seamless loop -->
        <span v-for="(logo, i) in marquee" :key="'dup-'+i" class="marquee-item">
          <v-icon left size="20" class="gradient-text">{{ logo.icon }}</v-icon>{{ logo.label }}
        </span>
      </div>
    </section>

    <!-- FEATURES -->
    <section id="features" class="section">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-6">
            <h2 class="section-title">O que você consegue fazer</h2>
            <p class="section-sub">Tudo que precisa para tirar suas ideias do papel, em um único lugar.</p>
          </v-col>

          <v-col
            v-for="(f, i) in features"
            :key="i"
            cols="12" sm="6" md="4"
            class="d-flex"
          >
            <v-card class="feature-card glass flex-grow-1" elevation="8" hover>
              <v-card-title>
                <div class="icon-ring mr-3">
                  <v-icon color="primary">{{ f.icon }}</v-icon>
                </div>
                <div class="title">&nbsp;{{ f.title }}</div>
              </v-card-title>
              <v-card-text class="pt-2">
                <p class="mb-4">{{ f.desc }}</p>

                <div class="chips" style="display: flex; flex-wrap: wrap; gap: 1px; justify-content: space-evenly;">
                  <v-chip v-for="(tag, ti) in f.tags" :key="ti" small outlined class="mr-1 mb-1">
                  {{ tag }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- TESTIMONIALS -->
    <section id="testimonials" class="section alt">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-6">
            <h2 class="section-title section-title-white">Quem está usando</h2>
            <p class="section-sub">Feedback real de quem já construiu com a gente.</p>
          </v-col>

          <v-col cols="12" md="4" v-for="(t, i) in testimonials" :key="i">
            <v-card class="testimonial-card" elevation="6">
              <v-card-text>
                <div class="stars">
                  <v-icon small color="amber">mdi-star</v-icon>
                  <v-icon small color="amber">mdi-star</v-icon>
                  <v-icon small color="amber">mdi-star</v-icon>
                  <v-icon small color="amber">mdi-star</v-icon>
                  <v-icon small color="amber">mdi-star</v-icon>
                </div>
                <p class="quote">“{{ t.quote }}”</p>
                <div class="persona">
                  <div class="avatar">{{ t.initials }}</div>
                  <div>
                    <div class="name">{{ t.name }}</div>
                    <div class="role">{{ t.role }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- HOW IT WORKS -->
    <section id="how" class="section">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-6">
            <h2 class="section-title">Como funciona</h2>
            <p class="section-sub">Comece em minutos. Sem complicação.</p>
          </v-col>

          <v-col v-for="(s, i) in steps" :key="i" cols="12" md="4">
            <div class="step">
              <div class="step-num">{{ i + 1 }}</div>
              <h3 class="step-title">{{ s.title }}</h3>
              <p class="step-desc">{{ s.desc }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CALL TO ACTION -->
    <section class="cta-section">
      <v-container>
        <v-row align="center" justify="center">
          <v-col cols="12" md="10" lg="8">
            <v-card class="cta-card" elevation="12">
              <v-card-text class="text-center py-8">
                <h3 class="cta-title">Pronto para destravar seu próximo projeto?</h3>
                <p class="cta-desc">Entre na plataforma e experimente nossos recursos de IA com segurança e performance.</p>
                <div class="mt-4">
                  <v-btn large color="primary" class="text-none font-weight-bold mr-2" depressed @click="$router.push('/chat')">
                    Entrar / Criar conta
                  </v-btn>
                  <v-btn large outlined class="text-none font-weight-bold" @click="scrollTo('#faq')" style="color: #e5e7eb;">
                    Dúvidas frequentes
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- FAQ -->
    <v-container id="faq" class="py-12">
      <v-row>
        <v-col cols="12" class="mb-6">
          <h2 class="font-weight-black display-1 mb-2 section-title-white">Perguntas frequentes</h2>
        </v-col>
        <v-col cols="12" md="12">
          <v-expansion-panels multiple>
            <v-expansion-panel v-for="(q, i) in faqs" :key="i">
              <v-expansion-panel-header>
                <span class="font-weight-bold">{{ q.q }}</span>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="py-1">{{ q.a }}</v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>


  </div>
</template>

<script>
export default {
  name: 'LandingPage',
  data: () => ({
    // animated counters (targets)
    stats: {
      users: 3200,
      uptime: 99.98,
      latency: 120
    },
    usersDisplay: '—',
    uptimeDisplay: '—',
    latencyDisplay: '—',
    countersStarted: false,
    assistenteChat: [
      "Quem é você? E que dia é hoje?",
      "Olá, sou o Direciona Tech IA, seu assistente virtual.",
      "Preciso de ajuda para minha empresa chegar a um novo patamar com IA.",
      "Claro! Posso ajudar com chatbots personalizados, geração de imagens e automações inteligentes. Quais são suas necessidades específicas?",
    ],
    marquee: [
      { icon: 'mdi-shield-check', label: 'Segurança de nível corporativo' },
      { icon: 'mdi-rocket-launch', label: 'Deploy contínuo' },
      { icon: 'mdi-cloud-outline', label: 'Infra escalável' },
      { icon: 'mdi-currency-usd', label: 'Custo otimizado' },
      { icon: 'mdi-webpack', label: 'SDKs prontos' },
    ],
    features: [
      { icon: 'mdi-robot-outline', title: 'Assistentes de Chat', desc: 'Conversas contextuais, memórias e ferramentas para integrar com seus dados.', tags: ['Contexto', 'Ferramentas', 'Memória'] },
      { icon: 'mdi-image-multiple-outline', title: 'Geração de Imagens', desc: 'Prompts avançados, variações e upscaler nativo para outputs nítidos.', tags: ['Variações', 'Upscale', 'Rascunho→Arte'] },
      { icon: 'mdi-api', title: 'API Segura', desc: 'Tokens por cliente, rate limit e auditoria para seu SaaS.', tags: ['JWT', 'Rate Limit', 'Logs'] },
      { icon: 'mdi-puzzle-outline', title: 'Integrações', desc: 'Webhooks e SDKs para conectar com suas aplicações.', tags: ['Webhooks', 'Node/.NET', 'CLI'] },
      { icon: 'mdi-chart-line', title: 'Dashboards', desc: 'Métricas em tempo real para decisões rápidas.', tags: ['Tempo real', 'KPIs'] },
      { icon: 'mdi-lock-check-outline', title: 'Privacidade', desc: 'Criptografia em trânsito e repouso, com governança.', tags: ['AES-256', 'IAM', 'LGPD'] },
    ],
    testimonials: [
      { quote: 'Reduzimos 40% do tempo de atendimento com um chatbot personalizado.', name: 'Marina A.', role: 'Head de CX — E-commerce', initials: 'MA' },
      { quote: 'Integração simples com nossa stack .NET e ótimos dashboards.', name: 'Carlos P.', role: 'CTO — Fintech', initials: 'CP' },
      { quote: 'As imagens geradas elevaram a qualidade dos nossos criativos.', name: 'Bruna S.', role: 'Designer — Agência', initials: 'BS' },
    ],
    steps: [
      { title: 'Crie sua conta', desc: 'Acesse o painel com email e senha (ou SSO corporativo).' },
      { title: 'Conecte seus dados', desc: 'Ative ferramentas, chaves e integrações em poucos cliques.' },
      { title: 'Construa com IA', desc: 'Implemente chatbots, imagens e automações em produção.' },
    ],
    faqs: [
      { q: 'Preciso de conta para usar Chat e Imagens?', a: 'Sim. Essas páginas exigem login. Use o botão “Entrar / Criar conta” para acessar.' },
      { q: 'A plataforma é compatível com meu backend?', a: 'Sim. Oferecemos SDKs e exemplos em Node.js, .NET e Python, além de webhooks.' },
      { q: 'Como a DIRECIONA TECH trata privacidade?', a: 'Seguimos boas práticas de segurança (TLS, armazenamento seguro) e padrões LGPD.' },
      { q: 'Existe plano gratuito?', a: 'Mantemos um plano inicial para testes com limites de uso. Consulte o painel após login.' },
    ],
    socials: [
      { icon: 'mdi-github', href: 'https://github.com/' },
      { icon: 'mdi-linkedin', href: 'https://www.linkedin.com/' },
      { icon: 'mdi-twitter', href: 'https://x.com/' },
    ],
  }),
  methods: {
    scrollTo(selector) {
      const el = document.querySelector(selector)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    onStatsIntersect(entries) {
      const isIntersecting = Array.isArray(entries) ? entries[0].isIntersecting : !!entries?.isIntersecting
      if (!isIntersecting || this.countersStarted) return
      this.countersStarted = true
      // animate counters
      this.animateCounter(0, this.stats.users, 1200, v => this.usersDisplay = this.formatUsers(v))
      this.animateCounter(80, this.stats.uptime, 1200, v => this.uptimeDisplay = this.formatPercent(v))
      this.animateCounter(300, this.stats.latency, 1200, v => this.latencyDisplay = this.formatMs(v))
    },
    animateCounter(from, to, duration, onUpdate) {
      const start = performance.now()
      const animate = now => {
        const p = Math.min(1, (now - start) / duration)
        const eased = 1 - Math.pow(1 - p, 3)
        const value = from + (to - from) * eased
        onUpdate(value)
        if (p < 1) requestAnimationFrame(animate)
      }
      requestAnimationFrame(animate)
    },
    formatUsers(v) {
      const n = Math.round(v)
      if (n >= 1000) return (n/1000).toFixed(1).replace('.0','') + 'k+'
      return String(n)
    },
    formatPercent(v) { return v.toFixed(2) + '%' },
    formatMs(v) { return Math.round(v) + 'ms' },
  },
}
</script>

<style scoped>
/* Page shell */
.landing-root { color: #0b1220; --brand-grad: linear-gradient(90deg, #60a5fa, #34d399, #a78bfa); }

/* HERO */
.hero-section {
  position: relative;
  overflow: hidden;
  padding: 96px 0 56px;
  background: radial-gradient(1200px 600px at 10% -10%, rgba(59,130,246,0.35), transparent 60%),
              radial-gradient(900px 500px at 90% -20%, rgba(99,102,241,0.35), transparent 60%),
              linear-gradient(180deg, #0f172a 0%, #0b1220 100%);
  color: #e5e7eb;
}

.hero-left { z-index: 2; }
.hero-right { z-index: 2; display: flex; justify-content: center; }

.hero-title {
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
  font-size: 44px;
  margin: 16px 0 10px;
}

@media (min-width: 960px) {
  .hero-title { font-size: 60px; }
}

.primary {
  background: var(--brand-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.gradient-text {
  background: var(--brand-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-subtitle {
  opacity: 0.9;
  font-size: 18px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(148,163,184,0.3);
  background: rgba(15,23,42,0.6);
  color: #cbd5e1;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 600;
  box-shadow: 0 0 0 3px rgba(255,255,255,0.04) inset;
}

.hero-cta { margin-top: 18px; }

.stats {
  margin-top: 28px;
  display: inline-flex;
  align-items: center;
  gap: 18px;
  padding: 10px 14px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(148,163,184,0.2);
  border-radius: 14px;
  backdrop-filter: blur(6px);
  transform: translateZ(0);
}

.stat { display: grid; text-align: left; }
.stat .n { font-weight: 800; color: #fff; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,0.6); }

.grid-bg {
  position: absolute; inset: 0; pointer-events: none;
  background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 40px 40px, 40px 40px;
  mask-image: linear-gradient(to bottom, black 60%, transparent);
}

.blob {
  position: absolute; filter: blur(40px); opacity: 0.45; border-radius: 50%;
  animation: float 12s ease-in-out infinite;
}

.blob-1 { width: 320px; height: 320px; background: #60a5fa; top: -60px; left: -40px; }
.blob-2 { width: 420px; height: 420px; background: #a78bfa; top: -120px; right: -80px; animation-delay: 3s; }

@keyframes float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(16px) } }

/* MARQUEE */
.marquee { border-top: 1px solid #e5e7eb33; border-bottom: 1px solid #e5e7eb33; background: #0b1220; }
.marquee-track {
  display: flex; gap: 28px; white-space: nowrap; overflow: hidden; padding: 14px 0; color: #cbd5e1;
}
.marquee-track { animation: scroll 28s linear infinite; }
.marquee-item { display: inline-flex; align-items: center; gap: 8px; opacity: 0.85; }

@keyframes scroll { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }

/* SECTION */
.section { padding: 72px 0; background-color: #f8fafc; }
.section-title-white { color: #e5e7eb; }
.section.alt { background: #0b1220; }
.section-title { font-weight: 900; font-size: 32px; letter-spacing: -0.02em; }
.section-sub { color: #a3a3a3; }

/* FEATURES */
.glass {
  background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03));
  border: 1px solid rgba(148,163,184,0.18);
  backdrop-filter: blur(8px);
}
.feature-card { transition: transform .25s ease, box-shadow .25s ease; border-radius: 16px; }
.feature-card:hover { transform: translateY(-4px) scale(1.01); box-shadow: 0 16px 40px rgba(0,0,0,0.25) !important; }
.avatar-chip { background: rgba(99,102,241,0.08); }
.feature-card .title { font-weight: 800; font-size: 20px; }
.icon-ring {
  width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center;
  background: radial-gradient(circle at 30% 30%, rgba(96,165,250,.35), transparent 40%),
              radial-gradient(circle at 70% 70%, rgba(167,139,250,.35), transparent 40%);
  border: 1px solid rgba(148,163,184,.25);
}

/* PREVIEW */
.preview-stack { position: relative; perspective: 1200px; }
.preview-card {
  width: 100%; max-width: 540px; margin: 0 auto; border-radius: 16px; overflow: hidden;
  border: 1px solid rgba(148,163,184,0.2);
  box-shadow: 0 30px 100px rgba(0,0,0,.35);
  transform: rotateY(-6deg) rotateX(2deg);
}
.preview-header { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: rgba(15,23,42,.8); }
.preview-header .dot { width: 10px; height: 10px; border-radius: 50%; }
.preview-header .dot.r { background: #ef4444 }
.preview-header .dot.y { background: #f59e0b }
.preview-header .dot.g { background: #22c55e }
.preview-title { color: #cbd5e1; font-weight: 700; margin-left: 6px; }
.preview-body { padding: 14px; background: rgba(2,6,23,.6); min-height: 260px; }
.msg { max-width: 90%; padding: 10px 12px; border-radius: 12px; margin-bottom: 10px; font-size: 14px; line-height: 1.4; }
.msg.me { margin-left: auto; background: rgba(59,130,246,.2); color: #e5e7eb; border: 1px solid rgba(59,130,246,.35); }
.msg.bot { margin-right: auto; background: rgba(148,163,184,.15); color: #e5e7eb; border: 1px solid rgba(148,163,184,.25); display: flex; align-items: flex-start; gap: 6px; }
.preview-footer { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: rgba(15,23,42,.8); }
.input-pill { flex: 1; margin-right: 8px; border-radius: 999px; padding: 8px 12px; border: 1px dashed rgba(148,163,184,.35); color: #94a3b8; display: inline-flex; align-items: center; gap: 6px; }

.preview-float { position: absolute; right: -4px; top: -12px; display: grid; gap: 10px; transform: translateZ(0);
}
.float-card { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 12px; background: rgba(2,6,23,.6); color: #e5e7eb; border: 1px solid rgba(148,163,184,.25); box-shadow: 0 10px 30px rgba(0,0,0,.35); }

/* STEPS */
.step { text-align: center; padding: 18px; }
.step-num {
  width: 56px; height: 56px; margin: 0 auto 10px; border-radius: 14px;
  display: grid; place-items: center; font-weight: 900; color: #0b1220;
  background: linear-gradient(135deg, #93c5fd, #86efac, #c4b5fd);
  box-shadow: 0 6px 20px rgba(59,130,246,0.25);
}
.step-title { font-weight: 800; margin-bottom: 6px; }
.step-desc { color: #475569; }

/* CTA */
.cta-section { padding: 40px 0 72px; background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%); }
.cta-card {
  border-radius: 18px;
  background: radial-gradient(800px 300px at 0% 0%, rgba(59,130,246,0.18), transparent 60%),
              radial-gradient(800px 300px at 100% 100%, rgba(167,139,250,0.18), transparent 60%),
              linear-gradient(180deg, #0f172a 0%, #0b1220 100%);
  color: #e5e7eb;
}
.cta-title { font-weight: 900; font-size: 28px; color: #e5e7eb; }
.cta-desc { opacity: 0.9; color: #7c7c7c; }

/* FAQ spacing overrides for nicer density */
#faq .v-expansion-panel-header { font-size: 16px; }

/* TESTIMONIALS */
.testimonial-card { border-radius: 16px; }
.testimonial-card .quote { color: #1f2937; font-weight: 600; margin: 12px 0 16px; }
.testimonial-card .persona { display: flex; align-items: center; gap: 10px; }
.testimonial-card .avatar { width: 36px; height: 36px; border-radius: 50%; background: #e5e7eb; color: #111827; display: grid; place-items: center; font-weight: 800; }
.testimonial-card .name { font-weight: 800; }
.testimonial-card .role { color: #6b7280; font-size: 14px; }

/* Utility */
.mb-6 { margin-bottom: 24px !important; }
.py-8 { padding-top: 32px !important; padding-bottom: 32px !important; }
.text-center { text-align: center !important; }
</style>

